import { RememberMeType } from 'types/gamp';
import { ComplianceSettings } from 'types/visa-sdk/checkout';
import {
  AssuranceData,
  AuthenticationStep,
  CountryCode,
  CurrencyCode,
  DataLevel,
  Locale,
  ReviewButtonAction
} from '../data';
import {
  AuthenticationContext,
  AuthenticationMethod,
  AuthenticationReason,
  DpaTransactionOptions,
  DynamicDataType,
  PayloadTypeIndicator
} from '../emvco';
import { ComboCardMode } from './payment-instrument';

type BooleanString = 'false' | 'true';

export type SetIntentRequestPayload = {
  buttonAction?: ReviewButtonAction;
  channel?: string;
  countryCode?: CountryCode;
  currencyCode?: CurrencyCode;
  enableThreeDS?: boolean;
  eventSource?: string;
  externalClientId?: string;
  locale?: Locale;
  merchantConfig?: {
    billingConstraints?: BillingConstraints;
    datalevel: string;
    displayName?: string;
    shippingConstraints?: ShippingConstraints;
    tokenizationSetup?: TokenizationSetup;
    threeDSSetup?: ThreeDSSetupOptions;
    websiteUrl?: string;
  };
  orderId?: string;
  srciProfile?: boolean;
  total?: number | string | null;
  visaCardsOnly?: boolean;
};

/* START GENAI@GHCOPILOT */
export type PutIntentRequestPayload = {
  assuranceData?: AssuranceData;
  authCode?: string;
  authenticationContext?: AuthenticationContext;
  authenticationMethod?: AuthenticationMethod;
  authorization?: string;
  codeChallenge?: string;
  complianceSettings?: ComplianceSettings;
  consumerNationalIdentifier?: string;
  cvmVerificationValue?: string;
  cvv?: string;
  dfpSessionId: string;
  dpaTransactionOptions?: DpaTransactionOptions;
  encPaymentInstrument?: string;
  encryptedBillingAddress?: string | null;
  encryptedShippingAddress?: string | null;
  installmentInfo?: InstallmentPlan | null;
  isBiometricsEnabledDevice?: boolean;
  isBiometricsFailed?: boolean;
  isFIDOSupported?: boolean;
  isThreeDSVerificationResponse?: boolean;
  isVdiDisabled?: boolean;
  paResPayload?: string;
  paymentInstrumentId: string;
  paymentInstrumentType?: CardType | ComboCardMode;
  platformType?: string;
  referenceURL: string;
  serverState?: string;
  shippingAddressId?: string | null;
  srcClientId?: string;
  srcCorrelationId?: string;
  srcDigitalCardId?: string;
  srciTransactionId?: string | null;
};
/* END GENAI@GHCOPILOT */

export type WidgetStyle = 'LIGHTBOX' | 'OVERLAY';
export type VerificationStatus = 'CHANGED' | 'CONSUMER_OVERRIDE' | 'NOT_VERIFIED' | 'VERIFIED';

export type Address = {
  accepted?: boolean;
  additionalLocation?: string;
  addressLine2?: string;
  billingAccepted?: boolean;
  city: string;
  countryCode: string;
  default?: boolean;
  error?: string;
  firstName: string;
  id?: string;
  lastName: string;
  lastUpdateTimestamp: number;
  line1?: string;
  line1raw?: string;
  line2?: string;
  line3?: string;
  masked?: boolean;
  nickName?: string;
  personName?: string;
  phone: string;
  phoneNumberCountryAlpha2Code?: string;
  phoneNumberCountryCode?: string;
  postalCode: string;
  stateProvinceCode: string;
  streetName?: string;
  streetNumber?: string;
  verificationStatus?: VerificationStatus;
};

export type ShippingAddress = {
  city?: string;
  countryCode?: string;
  line1?: string;
  line2?: string;
  line3?: string;
  postalCode?: string;
  state?: string;
};

export type CardArt = {
  baseImageFileName: string;
  brand?: string;
  country?: string;
  height: number;
  imageUrl: string;
  orientation?: string;
  paymentInstrumentGuid?: string;
  productType?: string;
  width: number;
};

export type ComboCardType = 'A' | 'C' | 'N';

export type InstallmentPlan = {
  acceptedTAndCVersion: string;
  annualPercentageRate: string;
  installmentAmount: string;
  installmentFrequency: 'BIMONTHLY' | 'BIWEEKLY' | 'MONTHLY' | 'WEEKLY';
  numberOfInstallments: string;
  termsAndConditionText: string;
  termsAndConditionURL: string;
  totalAmount: string;
  totalFees: string;
  transactionAmount: string;
  transactionCurrency: string;
  vPlanAcceptanceId: string;
  vPlanId: string;
};

export type CardBrand =
  | 'AMEX'
  | 'DISCOVER'
  | 'ELECTRON'
  | 'ELO'
  | 'INVALID'
  | 'MASTERCARD'
  | 'UNKNOWN'
  | 'VISA';

export type CardType =
  | 'CHARGE'
  | 'COMBO'
  | 'CREDIT'
  | 'DEBIT'
  | 'DEBIT/CREDIT'
  | 'DEFERRED DEBIT'
  | 'NONE'
  | 'PREPAID';

export type PaymentType = {
  cardBrand: CardBrand;
  cardType: CardType;
};

export type CollectBillingAddressType =
  | 'CNTRY'
  | 'FULL'
  | 'NONE'
  | 'PARTIAL'
  | 'POSTALCNTRY'
  | undefined;

export type BillingConstraints = {
  acceptedRegions?: AcceptedRegions;
  collectBillingAddress?: CollectBillingAddressType;
};

export type PaymentConstraints = {
  acceptCanadianVISADebit?: boolean;
  paymentCurrency?: string;
  paymentTypes?: {
    paymentType?: Array<{ cardBrand: CardBrand }> | null;
  };
};

export type AddressRegion = { countryCode: string };
export type AcceptedRegions = { region: AddressRegion[] };

export type ShippingConstraints = {
  acceptedRegions?: AcceptedRegions;
  collectShipping: boolean;
};

export type ThreeDSSetupOptions = {
  threeDSActive?: BooleanString | boolean;
  threeDSSuppressChallenge?: BooleanString | boolean;
};

export type ThreeDsSetupEnumerated = 'NONE' | 'ONBEHALF' | 'SELF';

export type TokenizationSetup = {
  tokenCryptogramType?: DynamicDataType;
  tokenCryptogramTypes?: DynamicDataType[];
  enableTokenization: boolean;
};

export type MerchantConfig = {
  allowCustomBranding?: boolean;
  bannerURL?: string;
  billingConstraints?: BillingConstraints;
  buttonAnimationEnabled?: boolean;
  buttonPosition?: string;
  clientId?: string;
  consumerEmailAddressRequested?: boolean;
  consumerNameRequested?: boolean;
  consumerNationalIdentifierRequested?: boolean;
  consumerPhoneNumberRequested?: boolean;
  currencyFormat?: string;
  dataLevel: DataLevel | PayloadTypeIndicator;
  defaultProfile?: boolean;
  displayName?: string;
  enableUserDataPrefill?: boolean;
  externalClientId?: string;
  externalProfileId?: 'default';
  guestCheckout?: boolean;
  logo?: {
    displayName: string;
  };
  merchantCountryCode?: string;
  shouldOptimizeDCF?: boolean;
  partnerCountryCode?: string;
  paymentConstraints?: PaymentConstraints;
  referenceCallID?: string;
  referenceCallId?: string;
  shippingConstraints?: ShippingConstraints;
  srcDpaId?: string;
  threeDSSetup?: ThreeDSSetupOptions;
  tokenizationSetup?: TokenizationSetup;
  total?: string | null;
  websiteUrl?: string;
  widgetStyle?: WidgetStyle;
};

export type PaymentInstrument = {
  accepted?: boolean;
  billingAddress: Address;
  billingAddressId?: string;
  cardArts: {
    cardArt: Array<CardArt>;
  };
  cardDisplay?: CardType | null;
  cardError?: string | null;
  cardFirstName: string;
  cardLastName: string;
  combinationCard?: ComboCardType;
  comboCardSet: boolean;
  comboProcessMethodChanged?: boolean;
  creationTimestamp: number;
  default: boolean;
  expirationDate: {
    month: string;
    year: string;
  };
  expired: boolean;
  id: string;
  isInstallmentEligible?: boolean;
  installmentInfo?: Array<InstallmentPlan>;
  issuerBid: string;
  lastFourDigits: string;
  lastUsedTimeStamp?: number;
  masked?: boolean;
  nameOnCard: string;
  nickName?: string;
  paymentType: PaymentType;
  paymentAccountReference: string;
  verificationStatus: VerificationStatus;

  // Not sure whether these properties actually exist
  binSixDigits?: string;
  dcfs?: {
    dcfInfo?: { walletType?: string }[];
  };
  digitalCardId?: string;
  tokenId?: string;
};

export type GetIntentResponse = {
  cardArt?: CardArt;
  merchantConfig: MerchantConfig;
  paymentInstrument: Array<PaymentInstrument>;
  shippingAddress?: Array<Address>;
};

export type SetIntentResponse = {
  intentId: string;
  cardArt: object;
  consumer?: {
    countryCode: string;
    emailAddress: string;
    firstName: string;
    fullName: string;
    languageCode: string;
    lastName: string;
  };
  merchantConfig: MerchantConfig;
  modifiedAmount?: string;
  paymentInstrument?: PaymentInstrument[];
  shippingAddress: Address[];
};

export type RiskDecision =
  | 'ALLOW'
  | 'REQUIRED_BIOMETRIC'
  | 'REQUIRED_CVV'
  | 'REQUIRED_ISSUER_CTF'
  | 'REQUIRED_VISA_OTP'
  | 'REQUIRED3DS'
  | 'SUCCESS_3DS'
  | 'SUCCESS_BIOMETRICS'
  | 'SUCCESS_CVV'
  | 'SUCCESS_VISA_OTP';

type PurchaseIntentThreeDSResponse = {
  checkoutResponse: undefined;
  riskDecision: 'REQUIRED3DS';
  threeDSData: {
    acsURL: string;
    payload: string;
  };
  threeDSRequired: true;
};

export type UserDetailResponse = {
  vuser?: {
    userName?: string;
    emailAddress?: string;
  };
};

export type IssuerStepUpRequestType =
  | {
      identifier: string;
      method: 'APP-TO-APP';
      requestPayload: string;
      source: string;
      value: string;
    }
  | {
      identifier: string;
      method: 'CUSTOMERSERVICE';
      value: string;
    }
  | {
      identifier: string;
      method: 'OTPEMAIL';
      source: string;
      value: string;
    }
  | {
      identifier: string;
      method: 'OTPSMS';
      source: string;
      value: string;
    }
  | {
      identifier: string;
      method: 'OUTBOUNDCALL';
      value: string;
    };

// See design doc: https://visawiki.trusted.visa.com/x/UI2AMg
type PurchaseIntentIssuerOTPResponse = {
  issuerName: string;
  riskDecision: 'REQUIRED_ISSUER_CTF';
  threeDSRequired: false;
  issuerStepUpResponse: {
    stepUpRequest: Array<IssuerStepUpRequestType>;
    status: 'CHALLENGE';
    clientReferenceID: string;
    clientDeviceID: string;
  };
  checkoutResponse: undefined;
};

export type PurchaseIntentNormalResponse = {
  checkoutResponse: undefined;
  encKey: string;
  encPaymentData: string;
  partialPaymentInstrument: {
    lastFourDigits: string;
    paymentType: PaymentType;
  };
  partialShippingAddress: {
    countryCode: CountryCode;
    postalCode: string;
  };
  riskDecision: Exclude<
    RiskDecision,
    'REQUIRED_BIOMETRIC' | 'REQUIRED_ISSUER_CTF' | 'REQUIRED_VISA_OTP' | 'REQUIRED3DS'
  >;
  threeDSRequired: false;
};

type ValidationChannel = {
  validationChannelId: string;
  identityProvider: string;
  identityType: 'EMAIL' | 'SMS';
  maskedValidationChannel: string;
};

type PurchaseIntentVisaOTPResponse = {
  checkoutResponse: undefined;
  encKey: string;
  encPaymentData: string;
  idValidationSessionId: string;
  maskedValidationChannel: ValidationChannel;
  partialPaymentInstrument: {
    lastFourDigits: string;
    paymentType: PaymentType;
  };
  partialShippingAddress: {
    countryCode: CountryCode;
    postalCode: string;
  };
  riskDecision: 'REQUIRED_VISA_OTP';
  supportedValidationChannels: ValidationChannel[];
  threeDSRequired: false;
};

export type PurchaseIntentExternalSrciResponse = {
  checkoutResponse: string;
  riskDecision?: undefined;
};

type PurchaseIntentBiometricsResponse = {
  checkoutResponse: string;
  riskDecision: 'REQUIRED_BIOMETRIC';
};

export type ThreeDsResponseAssuranceData = {
  eci: string;
  verificationData?: {
    methodResults: {
      acsTransId: string;
      dsTransId: string;
      transStatus: string;
    };
  }[];
};

export type PurchaseIntentThreeDsTwoResponse = {
  assuranceData?: ThreeDsResponseAssuranceData;
  checkoutResponse?: undefined;
  maskedCard?: MaskedCard;
  riskDecision?: undefined;
};

export type MaskedCard = {
  consumerDataIssuerManaged: boolean;
  srcDigitalCardId?: undefined;
  panBin?: undefined;
  panLastFour?: undefined;
  panExpirationMonth?: undefined;
  panExpirationYear?: undefined;
  paymentAccountReference: string;
  digitalCardData: DigitalCardData;
  dateofCardCreated: number;
  maskedBillingAddress: MaskedBillingAddress;
  eligible: true;
};

export type DigitalCardData = {
  artHeight: string;
  artUri: string;
  artWidth: string;
  authenticationMethods: AuthenticationMethod[];
  authenticationStep?: AuthenticationStep;
  descriptorName: string;
  pendingEvents: string[];
  presentationName: string;
  status: string;
};

export type MaskedBillingAddress = {
  srcConsumerId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  emailAddress?: string;
  mobileNumber: undefined;
  countryCode: undefined;
  languageCode: undefined;
  status: undefined;
};

export type PurchaseIntentIntegratedIssuerOTPResponse = {
  checkoutResponse: string;
  riskDecision?: undefined;
};

export type PurchaseIntentResponse =
  | PurchaseIntentBiometricsResponse
  | PurchaseIntentExternalSrciResponse
  | PurchaseIntentIntegratedIssuerOTPResponse
  | PurchaseIntentIssuerOTPResponse
  | PurchaseIntentNormalResponse
  | PurchaseIntentThreeDSResponse
  | PurchaseIntentThreeDsTwoResponse
  | PurchaseIntentVisaOTPResponse;

export enum ErrorCode {
  DECLINED_3DS2 = 1157,
  T2D_DECLINED = 1208,
  CV_DECLINED = 1209
}

export type GampRiskData = {
  isCardEnrollment: boolean;
  rememberMe: RememberMeType;
  authContextReasons: AuthenticationReason | 'UNDEFINED';
  riskDecision: RiskDecision;
};

export type EnrollResponse = {
  cardBrand: string;
  encryptedCard: string;
};
